<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { computed, ref, watch } from 'vue';
import { VCard } from 'vuetify/components';
import { useTheme } from 'vuetify';
import DialogCloseButton from './DialogCloseButton.vue';

const { xs: isPhone } = useDisplay();

type Emits = {
  (e: 'close'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: boolean;
  fullHeight?: boolean;
  title?: string;
  color?: string;
  extraClasses?: string[];
  dialogLabel?: string;
  closeBtnColor?: string;
  topBarRef?: HTMLElement | null;
  hideTopbar?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  fullHeight: false,
  topBarRef: null,
  extraClasses: () => ['rounded-t-xl', 'px-4', 'py-4'],
});

// Touch functionality variables
const drawerContent = ref<InstanceType<typeof VCard> | null>(null);
const internalTopBarRef = ref<any>(null);
const startY = ref<number | undefined>(0);
const endY = ref(0);
const swipeDistance = ref(0);
const isTopbarTouch = ref(false);

// Use provided topBarRef or fall back to internal ref for default slot
const activeTopBarRef = computed(() => {
  if (props.topBarRef) {
    return props.topBarRef;
  }
  return internalTopBarRef.value?.$el || null;
});

const isScrolledToTop = () => {
  if (!drawerContent.value) {
    return false;
  }

  const cardElement = drawerContent.value.$el;
  if (!cardElement) {
    return false;
  }
  if (cardElement.scrollTop > 0) {
    return false;
  }

  // Find all elements that might be scrollable
  const allElements = cardElement.querySelectorAll('*');
  for (const element of allElements) {
    // Check if element is actually scrollable by comparing scrollHeight to clientHeight
    if (element.scrollHeight > element.clientHeight && element.scrollTop > 0) {
      return false;
    }
  }

  return true;
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    }
  },
  { immediate: true },
);

const handleClose = () => {
  document.body.style.overscrollBehavior = 'auto';
  document.documentElement.style.overscrollBehavior = 'auto';
  emit('close');
};

const isTouchInTopbar = (event: TouchEvent) => {
  const topbarEl = activeTopBarRef.value;
  if (!topbarEl) {
    return false;
  }

  const target = event.target as Element;
  return topbarEl.contains(target);
};

const onTouchStart = (event: TouchEvent) => {
  isTopbarTouch.value = isTouchInTopbar(event);

  const scrolledToTop = isScrolledToTop();

  // Allow drag if: topbar touch (when topBarRef provided) OR content scrolled to top
  if (isTopbarTouch.value || scrolledToTop) {
    startY.value = event.touches[0].clientY;
  } else {
    startY.value = undefined;
  }
};

const onTouchMove = (event: TouchEvent) => {
  if (!startY.value) return;
  endY.value = event.touches[0].clientY;
  swipeDistance.value = endY.value - startY.value > 0 ? endY.value - startY.value : 0;
  // Prevent default if: topbar touch OR (no topBarRef and scrolled to top)
  if (swipeDistance.value > 50 && (isTopbarTouch.value || isScrolledToTop())) {
    if (event.cancelable) {
      event.preventDefault();
    }
  }
};

const onTouchEnd = () => {
  if (!startY.value) {
    endY.value = 0;
    isTopbarTouch.value = false;
    return;
  }

  const swipeThreshold = 50;
  if (swipeDistance.value > swipeThreshold) {
    endY.value = 0;
    handleClose();
  }

  setTimeout(() => {
    swipeDistance.value = 0;
    isTopbarTouch.value = false;
  }, 300);
};

// Dynamic styles for drawer movement during swipe
const drawerContainerStyles = computed(() => {
  if (swipeDistance.value === 0) return {};
  const transform = `translateY(${swipeDistance.value}px)`;
  return {
    transform,
    transition: swipeDistance.value === 0 ? 'transform 0.3s ease-out' : 'none',
  };
});

const currentTheme = computed(() => useTheme().global.name.value);
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    :height="'100%'"
    :width="'100vh'"
    :max-width="'100%'"
    :close-on-content-click="false"
    :persistent="false"
    :fullscreen="isPhone"
    transition="dialog-bottom-transition"
    class="dialog ma-0"
    :class="{
      'mt-16': isPhone,
    }"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
    @close="handleClose"
  >
    <v-card
      ref="drawerContent"
      :theme="currentTheme"
      :color="color || 'surface-container'"
      class="drawer-content overflow-hidden"
      :class="[extraClasses, fullHeight ? 'h-100vh' : 'h-auto']"
      :style="drawerContainerStyles"
      :ripple="false"
      @touchstart.capture="onTouchStart"
      @touchmove.capture="onTouchMove"
      @touchend.capture="onTouchEnd"
      @click.stop
    >
      <div v-if="!hideTopbar" class="drawer-topbar">
        <slot name="topbar">
          <v-card-title ref="internalTopBarRef" class="d-flex justify-space-between align-center px-0 py-0">
            <template v-if="title">
              <v-spacer />
              {{ title }}
              <v-spacer />
            </template>
            <slot name="close">
              <DialogCloseButton :color="closeBtnColor" @click="emit('close')" />
            </slot>
          </v-card-title>
        </slot>
      </div>
      <slot name="default"></slot>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.h-100vh {
  height: 100vh !important;
}
:deep(.v-overlay__content) {
  top: auto !important;
  bottom: 0;
  height: auto !important;
  margin-bottom: 0 !important;
}
.drawer-content {
  // Match DrawerBottom's scrolling behavior
  overflow-y: scroll;
  overscroll-behavior: none;

  // Disable ripple effects and unwanted touch animations
  :deep(.v-ripple__container) {
    display: none !important;
  }

  // Ensure the card doesn't act as an interactive surface
  pointer-events: auto;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
}

.drawer-topbar {
  padding-block: 8px;
}

.v-card {
  padding-bottom: max(env(safe-area-inset-bottom, 0px), 16px) !important;

  // Disable any potential ripple effects on the card itself
  :deep(.v-ripple__container) {
    display: none !important;
  }
}
</style>
